<template>
	<div class="net-toolbox" :class="{ 'is-scanning': scanning }">
		<!-- Header -->
		<div class="net-header">
			<div class="net-header-left">
				<div class="net-header-icon">
					<v-icon size="18" color="white">mdi-lan</v-icon>
				</div>
				<div class="net-header-text">
					<span class="net-title">{{ $t('yealink-net-title') }}</span>
				</div>
			</div>
			<div class="net-header-right">
				<span v-if="isDone" class="net-fresh" :title="freshTitle">
					<v-icon x-small color="#16a34a" class="mr-1">mdi-check-decagram</v-icon>
					{{ $t('yealink-net-fresh', { time: formatTime(scan.finishedAt) }) }}
					<v-icon v-if="scan.historyEntryType" x-small :color="scan.historyEntryType === 'detected' ? '#f59e0b' : '#00acc1'" class="ml-1">
						{{ scan.historyEntryType === 'detected' ? 'mdi-source-branch' : 'mdi-content-save-check' }}
					</v-icon>
				</span>
				<v-btn
					small
					text
					class="net-header-btn"
					:disabled="scanning"
					@click="runScan"
				>
					<v-icon small left>mdi-radar</v-icon>
					{{ $t('yealink-net-rescan') }}
				</v-btn>
				<v-btn
					small
					text
					class="net-header-btn"
					:class="{ 'is-active': historyOpen }"
					@click="historyOpen = !historyOpen; collapsed = false"
				>
					<v-icon small left>mdi-history</v-icon>
					{{ $t('yealink-net-history') }}
					<span v-if="history.entryCount" class="net-count-pill">{{ history.entryCount }}</span>
				</v-btn>
				<v-btn icon small class="net-collapse-btn" @click="collapsed = !collapsed">
					<v-icon small>{{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
				</v-btn>
			</div>
		</div>

		<!-- Collapsed summary strip -->
		<div v-if="collapsed && isDone" class="net-collapsed-strip" @click="collapsed = false">
			<span class="net-mini-badge" :style="{ '--badge-color': transportColor(summary.transport) }">{{ transportLabel(summary.transport) }}</span>
			<span class="net-mini-badge" :style="{ '--badge-color': stunColor(summary.stun) }">{{ $t('yealink-net-stun') }} {{ stunLabel(summary.stun) }}</span>
			<span class="net-collapsed-text">{{ $t('yealink-net-phones-of', { count: summary.scanned, total: summary.total }) }}</span>
			<span v-if="attention.length" class="net-collapsed-warn">
				<v-icon x-small color="#f59e0b" class="mr-1">mdi-alert-circle</v-icon>{{ attention.length }}
			</span>
		</div>

		<!-- Scanning (fresh data only, never show stale state) -->
		<div v-if="scanning || (!scan && !scanError)" class="net-scanning">
			<v-progress-linear
				:value="progress"
				:indeterminate="!scan || !scan.total"
				height="6"
				rounded
				color="#00acc1"
				background-color="rgba(0, 172, 193, 0.12)"
			/>
			<div class="net-scanning-text">
				<v-progress-circular
					indeterminate
					size="14"
					width="2"
					color="#00acc1"
					class="mr-2"
				/>
				<span>{{ $t('yealink-net-scanning', { completed: scan ? scan.completed : 0, total: scan ? scan.total : '…' }) }}</span>
				<span class="net-scanning-hint">{{ $t('yealink-net-scanning-hint') }}</span>
			</div>
			<div v-if="scan" class="net-scan-pills">
				<span
					v-for="d in scan.devices"
					:key="d.deviceId"
					class="net-scan-pill"
					:class="`state-${d.state}`"
					:title="d.name || d.mac"
				>
					<v-progress-circular
						v-if="d.state === 'pending'"
						indeterminate
						size="10"
						width="2"
						color="#00acc1"
					/>
					<v-icon v-else x-small :color="stateColor(d.state)">{{ stateIcon(d.state) }}</v-icon>
					<span class="net-scan-pill-name">{{ d.name || d.mac }}</span>
					<span v-if="d.state === 'ok'" class="net-scan-pill-value" :style="{ color: transportColor(d.transport) }">{{ transportLabel(d.transport) }}</span>
				</span>
			</div>
		</div>

		<!-- Scan error -->
		<v-alert
			v-else-if="scanError"
			type="error"
			outlined
			dense
			class="ma-3 mb-2"
		>
			<div class="d-flex align-center justify-space-between flex-wrap">
				<span class="text-body-2">{{ scanError }}</span>
				<v-btn small text color="error" @click="runScan">
					<v-icon small left>mdi-refresh</v-icon>
					{{ $t('yealink-net-rescan') }}
				</v-btn>
			</div>
		</v-alert>

		<!-- Results -->
		<div v-else-if="!collapsed" class="net-body">
			<div class="net-card">
				<!-- Current state -->
				<div class="net-state">
					<div class="net-tiles">
						<div class="net-tile" :style="{ '--tile-color': transportColor(summary.transport) }">
							<span class="net-tile-label">{{ $t('yealink-net-transport') }}</span>
							<span class="net-tile-value">{{ transportLabel(summary.transport) }}</span>
							<span class="net-tile-sub">
								<template v-if="summary.scanned">{{ $t('yealink-net-phones-of', { count: dominantTransportCount, total: summary.scanned }) }}</template>
								<template v-else>{{ $t('yealink-net-no-scanned') }}</template>
							</span>
						</div>
						<div class="net-tile" :style="{ '--tile-color': stunColor(summary.stun) }">
							<span class="net-tile-label">{{ $t('yealink-net-stun') }}</span>
							<span class="net-tile-value">{{ stunLabel(summary.stun) }}</span>
							<span class="net-tile-sub" :title="summary.stun === true ? stunServerDisplay : ''">
								<template v-if="summary.stun === 'mixed'">{{ summary.stunOn }} {{ $t('yealink-net-stun-on') }} · {{ summary.stunOff }} {{ $t('yealink-net-stun-off') }}</template>
								<template v-else-if="summary.stun === true">{{ stunServerDisplay }}</template>
								<template v-else-if="summary.stun === false">{{ $t('yealink-net-stun-none') }}</template>
								<template v-else>—</template>
							</span>
						</div>
					</div>

					<!-- Distribution -->
					<div class="net-dist">
						<div class="net-dist-bar">
							<span
								v-for="seg in distribution"
								:key="seg.key"
								class="net-dist-seg"
								:style="{ width: seg.percent + '%', background: seg.color }"
								:title="`${seg.label}: ${seg.count}`"
							/>
						</div>
						<div class="net-dist-legend">
							<span v-for="seg in distribution" :key="seg.key" class="net-legend-item">
								<span class="net-legend-dot" :style="{ background: seg.color }" />
								<strong>{{ seg.count }}</strong> {{ seg.label }}
							</span>
							<button
								v-if="attention.length"
								type="button"
								class="net-attention-toggle"
								@click="attentionOpen = !attentionOpen"
							>
								<v-icon x-small color="#f59e0b" class="mr-1">mdi-alert-circle</v-icon>
								{{ $t('yealink-net-attention-count', { count: attention.length }) }}
								<v-icon x-small color="#92400e">{{ attentionOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
							</button>
						</div>
					</div>

					<!-- Attention -->
					<div v-if="attention.length && attentionOpen" class="net-attention">
						<div v-for="d in attention" :key="d.deviceId" class="net-attention-row">
							<span class="net-attention-name">{{ d.name || d.mac }}</span>
							<span class="net-attention-reason">{{ d.reason }}</span>
							<span class="net-attention-actions">
								<v-btn
									icon
									x-small
									:title="$t('yealink-net-repush', repairTarget(d) ? { transport: transportLabel(repairTarget(d).transport), stun: stunLabel(repairTarget(d).stun) } : {})"
									:disabled="!repairTarget(d) || repairingIds.includes(d.deviceId) || applying"
									:loading="repairingIds.includes(d.deviceId)"
									@click="repairDevice(d)"
								>
									<v-icon x-small color="#00838f">mdi-cloud-sync</v-icon>
								</v-btn>
								<v-btn
									icon
									x-small
									:title="$t('yealink-reboot')"
									@click="$emit('reboot-device', d.deviceId)"
								>
									<v-icon x-small color="#d97706">mdi-restart</v-icon>
								</v-btn>
							</span>
						</div>
					</div>
				</div>

				<div class="net-divider" />

				<!-- Switch -->
				<div class="net-switch">
					<div class="net-switch-row">
						<span class="net-switch-label">{{ $t('yealink-net-switch-title') }}</span>
						<div class="net-transport-options">
							<button
								v-for="opt in visibleTransportOptions"
								:key="opt.value"
								type="button"
								class="net-transport-btn"
								:class="{ 'is-selected': selectedTransport === opt.value, 'is-current': summary.transport === opt.value, 'is-advanced': opt.advanced }"
								:style="{ '--opt-color': opt.color }"
								:disabled="applying"
								:title="opt.title"
								@click="selectTransport(opt.value)"
							>
								<v-icon x-small :color="selectedTransport === opt.value ? 'white' : opt.color">{{ opt.icon }}</v-icon>
								{{ opt.label }}
								<span v-if="summary.transport === opt.value" class="net-transport-current" />
							</button>
							<button
								type="button"
								class="net-advanced-btn"
								:class="{ 'is-open': advancedOpen }"
								:title="$t('yealink-net-advanced')"
								@click="advancedOpen = !advancedOpen"
							>
								<v-icon x-small>mdi-tune-variant</v-icon>
							</button>
						</div>
					</div>

					<div class="net-switch-row net-stun-row" :class="{ 'is-disabled': selectedTransport === null }">
						<v-switch
							v-model="stunEnabled"
							inset
							dense
							hide-details
							color="#16a34a"
							class="net-stun-switch"
							:disabled="selectedTransport === null || applying"
						>
							<template #label>
								<span class="net-stun-label">
									<strong>{{ $t('yealink-net-stun') }}</strong>
									<span class="net-stun-server">{{ stunServer }}</span>
								</span>
							</template>
						</v-switch>
						<span
							v-if="selectedTransport !== null"
							class="net-stun-default"
							:class="{ 'is-override': stunEnabled !== defaultStunFor(selectedTransport) }"
							:title="stunEnabled !== defaultStunFor(selectedTransport) ? $t('yealink-net-stun-override') : (defaultStunFor(selectedTransport) ? $t('yealink-net-stun-default-on', { transport: transportLabel(selectedTransport) }) : $t('yealink-net-stun-default-off', { transport: transportLabel(selectedTransport) }))"
						>
							<v-icon x-small>{{ stunEnabled !== defaultStunFor(selectedTransport) ? 'mdi-pencil' : 'mdi-auto-fix' }}</v-icon>
						</span>
						<span v-if="selectedTransport === 2" class="net-tls-warn">
							<v-icon x-small color="#b91c1c" class="mr-1">mdi-flask-outline</v-icon>{{ $t('yealink-net-tls-unsupported') }}
						</span>
					</div>

					<div class="net-switch-row net-actions">
						<v-btn
							small
							depressed
							color="#00acc1"
							class="white--text net-apply-btn"
							:disabled="selectedTransport === null || !scan.total || applying || rescanCountdown !== null"
							:loading="applying"
							@click="applyDialog = true"
						>
							<v-icon small left>mdi-cloud-upload</v-icon>
							{{ $t('yealink-net-apply', { count: scan.total }) }}
						</v-btn>
						<v-btn
							small
							outlined
							color="#d97706"
							class="net-reboot-btn"
							:disabled="!onlineCount || rebooting || applying"
							:loading="rebooting"
							@click="rebootDialog = true"
						>
							<v-icon small left>mdi-restart</v-icon>
							{{ $t('yealink-net-reboot-all') }}
							<span class="net-count-pill warn">{{ onlineCount }}</span>
						</v-btn>
					</div>

					<div v-if="selectedTransport !== null" class="net-impact">
						<span class="net-impact-item change">
							<v-icon x-small color="#f59e0b" class="mr-1">mdi-swap-horizontal</v-icon>
							{{ $t('yealink-net-impact-change', { count: impact.change }) }}
						</span>
						<span class="net-impact-item same">
							<v-icon x-small color="#16a34a" class="mr-1">mdi-check</v-icon>
							{{ $t('yealink-net-impact-same', { count: impact.same }) }}
						</span>
						<span v-if="impact.offline" class="net-impact-item offline">
							<v-icon x-small color="#9ca3af" class="mr-1">mdi-power-plug-off</v-icon>
							{{ $t('yealink-net-impact-offline', { count: impact.offline }) }}
						</span>
					</div>
				</div>
			</div>

			<!-- Result banners -->
			<v-alert
				v-if="applyResult"
				:type="applyResult.failed ? 'warning' : 'success'"
				outlined
				dense
				class="net-banner"
			>
				<div class="d-flex align-center flex-wrap net-banner-row">
					<div class="d-flex flex-column flex-grow-1">
						<span class="text-body-2 font-weight-bold">
							{{ $t('yealink-net-apply-success', { pushed: applyResult.pushed, total: applyResult.total }) }}
							— {{ transportLabel(applyResult.transport) }} · {{ $t('yealink-net-stun') }} {{ applyResult.stun ? $t('yealink-net-stun-on') : $t('yealink-net-stun-off') }}
						</span>
						<span v-for="r in applyResult.results.filter(x => x.result === 'failed')" :key="r.deviceId" class="text-caption error--text">
							{{ r.name || r.mac }}: {{ r.error }}
						</span>
					</div>
					<div v-if="rescanCountdown !== null" class="net-countdown">
						<v-progress-circular
							:value="(rescanCountdown / rescanTotal) * 100"
							:rotate="-90"
							size="30"
							width="3"
							color="#00acc1"
						>
							<span class="net-countdown-num">{{ rescanCountdown }}</span>
						</v-progress-circular>
						<span class="net-countdown-text">{{ $t('yealink-net-rescan-in', { seconds: rescanCountdown }) }}</span>
						<v-btn x-small text color="#00acc1" @click="runScan">
							{{ $t('yealink-net-rescan-now') }}
						</v-btn>
					</div>
				</div>
			</v-alert>

			<v-alert
				v-if="rebootResult"
				:type="rebootResult.failed ? 'warning' : 'success'"
				outlined
				dense
				dismissible
				class="net-banner"
				@input="rebootResult = null"
			>
				<span class="text-body-2 font-weight-bold">
					{{ $t('yealink-net-reboot-all-success', { rebooted: rebootResult.rebooted, skipped: rebootResult.skipped, failed: rebootResult.failed }) }}
				</span>
			</v-alert>

			<!-- History -->
			<div v-if="historyOpen" class="net-history">
				<div class="net-history-header">
					<v-icon small color="#5a6577" class="mr-1">mdi-history</v-icon>
					<span class="net-history-title">{{ $t('yealink-net-history') }}</span>
					<span v-if="history.entryCount" class="net-history-count">{{ history.entryCount }}</span>
					<v-progress-circular
						v-if="history.loading"
						indeterminate
						size="12"
						width="2"
						color="#00acc1"
						class="ml-2"
					/>
				</div>

				<div v-if="!history.loading && !history.entries.length" class="net-history-empty">
					{{ $t('yealink-net-history-empty') }}
				</div>

				<div v-else class="net-timeline">
					<div
						v-for="entry in visibleHistory"
						:key="entry.id"
						class="net-entry"
						:class="[`type-${entry.type}`, { 'is-open': expandedEntries[entry.id] }]"
					>
						<div class="net-entry-dot">
							<v-icon x-small color="white">{{ entryIcon(entry.type) }}</v-icon>
						</div>
						<div class="net-entry-body" @click="toggleEntry(entry.id)">
							<div class="net-entry-top">
								<span class="net-entry-title">{{ entryTitle(entry) }}</span>
								<span class="net-entry-badges">
									<span class="net-mini-badge" :style="{ '--badge-color': transportColor(entry.transport) }">{{ transportLabel(entry.transport) }}</span>
									<span class="net-mini-badge" :style="{ '--badge-color': stunColor(entry.stun) }">{{ $t('yealink-net-stun') }} {{ stunLabel(entry.stun) }}</span>
								</span>
							</div>
							<div class="net-entry-meta">
								<span class="net-entry-date" :title="fromNow(entry.at)">
									<v-icon x-small class="mr-1">mdi-clock-outline</v-icon>{{ formatDateTime(entry.at) }}
								</span>
								<span v-if="entry.by" class="net-entry-by" :title="entry.by.email">
									<v-icon x-small class="mr-1">mdi-account</v-icon>{{ entry.by.name }}
								</span>
								<span class="net-entry-devices">
									<v-icon x-small class="mr-1">mdi-deskphone</v-icon>{{ $t('yealink-net-history-phones', { count: (entry.devices || []).length }) }}
								</span>
								<span v-if="entry.changes && entry.changes.length" class="net-entry-changes">
									<v-icon x-small class="mr-1">mdi-delta</v-icon>{{ $t('yealink-net-history-changes', { count: entry.changes.length }) }}
								</span>
								<v-icon x-small class="net-entry-chevron">{{ expandedEntries[entry.id] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
							</div>

							<div v-if="expandedEntries[entry.id]" class="net-entry-details" @click.stop>
								<template v-if="entry.changes && entry.changes.length">
									<div v-for="c in entry.changes" :key="c.deviceId" class="net-change-row">
										<span class="net-change-name">{{ c.name || c.mac }}</span>
										<span class="net-change-from">
											<template v-if="c.from">{{ transportLabel(c.from.transport) }} · {{ stunShort(c.from.stun) }}</template>
											<template v-else>—</template>
										</span>
										<v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
										<span class="net-change-to" :style="{ color: transportColor(c.to.transport) }">{{ transportLabel(c.to.transport) }} · {{ stunShort(c.to.stun) }}</span>
										<span v-if="c.result" class="net-change-result" :class="c.result">{{ c.result === 'pushed' ? $t('yealink-net-history-pushed') : $t('yealink-net-history-failed') }}</span>
									</div>
								</template>
								<template v-else>
									<div v-for="d in entry.devices" :key="d.deviceId" class="net-change-row">
										<span class="net-change-name">{{ d.name || d.mac }}</span>
										<span class="net-change-to" :style="{ color: transportColor(d.transport) }">{{ transportLabel(d.transport) }} · {{ stunShort(d.stun) }}</span>
									</div>
								</template>
							</div>
						</div>
					</div>

					<v-btn
						v-if="history.entries.length > historyLimit"
						x-small
						text
						color="#00acc1"
						class="mt-1"
						@click="showAllHistory = !showAllHistory"
					>
						{{ showAllHistory ? $t('yealink-net-history-show-less') : $t('yealink-net-history-show-all', { count: history.entries.length }) }}
					</v-btn>
				</div>
			</div>
		</div>

		<!-- Apply confirmation -->
		<v-dialog v-model="applyDialog" max-width="520" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="cyan white--text">
					<v-icon color="white" class="mr-2">mdi-swap-horizontal-bold</v-icon>
					{{ $t('yealink-net-apply-title') }}
					<v-spacer />
					<v-btn icon small dark @click="applyDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<div class="net-confirm-target">
						<span class="net-confirm-big" :style="{ color: transportColor(selectedTransport) }">{{ transportLabel(selectedTransport) }}</span>
						<span class="net-confirm-plus">+</span>
						<span class="net-confirm-big" :style="{ color: stunColor(stunEnabled) }">{{ $t('yealink-net-stun') }} {{ stunLabel(stunEnabled) }}</span>
					</div>
					<p class="mb-2">
						{{ $t('yealink-net-apply-confirm', { site: scan ? scan.siteName : '', transport: transportLabel(selectedTransport), stun: stunLabel(stunEnabled) }) }}
					</p>
					<p v-if="selectedTransport === 2" class="text-caption mb-2">
						<v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ $t('yealink-net-apply-tls-note') }}
					</p>
					<div class="net-impact net-impact-dialog">
						<span class="net-impact-item change">{{ $t('yealink-net-impact-change', { count: impact.change }) }}</span>
						<span class="net-impact-item same">{{ $t('yealink-net-impact-same', { count: impact.same }) }}</span>
						<span v-if="impact.offline" class="net-impact-item offline">{{ $t('yealink-net-impact-offline', { count: impact.offline }) }}</span>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="applyDialog = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn color="cyan" dark :loading="applying" @click="confirmApply">
						<v-icon left small>mdi-cloud-upload</v-icon>
						{{ $t('yealink-net-apply', { count: scan ? scan.total : 0 }) }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Reboot all confirmation -->
		<v-dialog v-model="rebootDialog" max-width="460" persistent content-class="devops-subdialog">
			<v-card>
				<v-card-title class="warning white--text">
					<v-icon color="white" class="mr-2">mdi-restart</v-icon>
					{{ $t('yealink-net-reboot-all-title') }}
					<v-spacer />
					<v-btn icon small dark @click="rebootDialog = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text class="pt-4">
					<v-alert type="warning" outlined dense class="mb-3">
						{{ $t('yealink-net-reboot-all-confirm', { count: onlineCount, site: scan ? scan.siteName : '' }) }}
					</v-alert>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="rebootDialog = false">
						{{ $t('cancel') }}
					</v-btn>
					<v-btn color="warning" :loading="rebooting" @click="confirmReboot">
						<v-icon left small>mdi-restart</v-icon>
						{{ $t('yealink-net-reboot-all') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import moment from "moment";

const TRANSPORT_COLORS = { 0: "#f59e0b", 1: "#2563eb", 2: "#16a34a", 3: "#7c3aed" };
const MIXED_COLOR = "#dc2626";
const UNKNOWN_COLOR = "#9ca3af";

export default {
	name: "YealinkNetworkToolbox",

	props: {
		siteId: {
			type: String,
			default: null
		}
	},

	data() {
		return {
			scan: null,
			scanning: false,
			scanError: null,
			pollTimer: null,
			scanRequestId: 0,

			selectedTransport: null,
			stunEnabled: false,
			stunServer: "global.stun.twilio.com",
			advancedOpen: false,
			attentionOpen: false,

			applying: false,
			applyDialog: false,
			applyResult: null,
			repairingIds: [],
			rescanCountdown: null,
			rescanTotal: 15,
			rescanTimer: null,

			rebooting: false,
			rebootDialog: false,
			rebootResult: null,

			historyOpen: false,
			collapsed: false,
			history: { loading: false, entries: [], entryCount: 0 },
			expandedEntries: {},
			showAllHistory: false,
			historyLimit: 3
		};
	},

	computed: {
		isDone() {
			return !!this.scan && this.scan.status === "done" && !this.scanning;
		},

		summary() {
			return this.scan?.summary || {
				total: 0, scanned: 0, offline: 0, errors: 0, byTransport: {}, stunOn: 0, stunOff: 0, stunMixed: 0, transport: null, stun: null
			};
		},

		progress() {
			if (!this.scan?.total) return 0;
			return Math.round((this.scan.completed / this.scan.total) * 100);
		},

		transportOptions() {
			return [
				{ value: 0, label: "UDP", icon: "mdi-lightning-bolt", color: TRANSPORT_COLORS[0], title: this.$t("yealink-net-udp-sub") },
				{ value: 1, label: "TCP", icon: "mdi-swap-horizontal", color: TRANSPORT_COLORS[1], title: this.$t("yealink-net-tcp-sub") },
				{ value: 2, label: "TLS", icon: "mdi-flask-outline", color: TRANSPORT_COLORS[2], title: this.$t("yealink-net-tls-unsupported"), advanced: true }
			];
		},

		visibleTransportOptions() {
			// TLS is unsupported: only shown behind "advanced", or when a phone already uses it
			return this.transportOptions.filter(o =>
				!o.advanced || this.advancedOpen || this.selectedTransport === o.value || this.summary.transport === o.value
			);
		},

		freshTitle() {
			if (!this.scan) return "";
			const parts = [this.formatDateTime(this.scan.finishedAt)];

			if (this.scan.historyEntryType === "detected") parts.push(this.$t("yealink-net-drift-recorded"));
			else if (this.scan.historyEntryType === "initial") parts.push(this.$t("yealink-net-initial-recorded"));
			return parts.join("\n");
		},

		scannedDevices() {
			return (this.scan?.devices || []).filter(d => d.state === "ok");
		},

		onlineCount() {
			return (this.scan?.devices || []).filter(d => d.deviceStatus?.toLowerCase() === "online").length;
		},

		dominantTransportCount() {
			const by = this.summary.byTransport || {};
			const label = this.transportLabel(this.summary.transport);

			if (this.summary.transport === "mixed") {
				return Math.max(0, ...Object.values(by));
			}
			return by[label] || 0;
		},

		stunServerDisplay() {
			const servers = new Set(this.scannedDevices.map(d => d.stunServer).filter(Boolean));

			if (!servers.size) return this.$t("yealink-net-stun-no-server");
			return [...servers].join(", ");
		},

		distribution() {
			const total = this.scan?.total || 0;

			if (!total) return [];
			const segs = [];
			const by = this.summary.byTransport || {};
			const order = ["UDP", "TCP", "TLS", "DNS-NAPTR", "MIXED"];

			for (const label of order) {
				if (by[label]) {
					const value = Object.keys(TRANSPORT_COLORS).find(k => this.transportLabel(Number(k)) === label);

					segs.push({ key: label, label, count: by[label], color: label === "MIXED" ? MIXED_COLOR : TRANSPORT_COLORS[value] });
				}
			}
			for (const label of Object.keys(by)) {
				if (!order.includes(label)) segs.push({ key: label, label, count: by[label], color: UNKNOWN_COLOR });
			}
			if (this.summary.offline) segs.push({ key: "offline", label: this.$t("yealink-net-offline"), count: this.summary.offline, color: "#cbd5e1" });
			if (this.summary.errors) segs.push({ key: "errors", label: this.$t("yealink-net-errors"), count: this.summary.errors, color: "#fb7185" });

			return segs.map(s => ({ ...s, percent: (s.count / total) * 100 }));
		},

		attention() {
			const rows = [];
			const site = this.summary.transport;

			for (const d of this.scan?.devices || []) {
				if (d.state === "error" || d.state === "timeout") {
					rows.push({ ...d, reason: this.$t("yealink-net-device-chip-error") });
				} else if (d.state === "ok") {
					if (d.stunMisconfigured) {
						rows.push({ ...d, reason: this.$t("yealink-net-misconfigured") });
					} else if (d.transport === "mixed") {
						rows.push({ ...d, reason: this.$t("yealink-net-mixed-lines") });
					} else if (site !== "mixed" && site !== null && d.transport !== site) {
						rows.push({ ...d, reason: this.$t("yealink-net-differs", { transport: this.transportLabel(d.transport) }) });
					}
				}
			}
			return rows;
		},

		impact() {
			let change = 0;
			let same = 0;
			let offline = 0;

			for (const d of this.scan?.devices || []) {
				if (d.state === "ok") {
					if (d.transport === this.selectedTransport && d.stun === this.stunEnabled) same++;
					else change++;
				} else if (d.state === "offline") {
					offline++;
				} else {
					change++;
				}
			}
			return { change, same, offline };
		},

		visibleHistory() {
			if (this.showAllHistory) return this.history.entries;
			return this.history.entries.slice(0, this.historyLimit);
		}
	},

	watch: {
		siteId() {
			this.runScan();
		},

		scanning(val) {
			this.$emit("scanning", val);
		}
	},

	mounted() {
		this.runScan();
		this.fetchHistory();
	},

	beforeDestroy() {
		this.stopPolling();
		this.stopRescanCountdown();
	},

	methods: {
		siteQuery() {
			return this.siteId ? `?siteId=${encodeURIComponent(this.siteId)}` : "";
		},

		stopPolling() {
			if (this.pollTimer) {
				clearTimeout(this.pollTimer);
				this.pollTimer = null;
			}
		},

		stopRescanCountdown() {
			if (this.rescanTimer) {
				clearInterval(this.rescanTimer);
				this.rescanTimer = null;
			}
			this.rescanCountdown = null;
		},

		// Phones need a moment to re-register after a push before the config export reflects it
		startRescanCountdown() {
			this.stopRescanCountdown();
			this.rescanCountdown = this.rescanTotal;
			this.rescanTimer = setInterval(() => {
				if (this.rescanCountdown === null) return;
				this.rescanCountdown--;
				if (this.rescanCountdown <= 0) {
					this.stopRescanCountdown();
					this.runScan();
				}
			}, 1000);
		},

		async runScan() {
			this.stopPolling();
			this.stopRescanCountdown();
			const requestId = ++this.scanRequestId;

			this.scanning = true;
			this.scanError = null;
			this.scan = null;
			this.applyResult = null;
			this.rebootResult = null;
			this.$emit("scan-update", null);

			try {
				const { data } = await this.axios.post(`/devops/yealink/network/scan${this.siteQuery()}`);

				if (requestId !== this.scanRequestId) return;
				await this.poll(data.scanId, requestId);
			} catch (err) {
				if (requestId !== this.scanRequestId) return;
				console.error("Failed to start network scan:", err);
				this.scanError = err.response?.data?.message || this.$t("yealink-net-scan-error");
				this.scanning = false;
			}
		},

		async poll(scanId, requestId) {
			try {
				const { data } = await this.axios.get(`/devops/yealink/network/scan/${scanId}`);

				if (requestId !== this.scanRequestId) return;
				this.scan = data;

				if (data.status === "running") {
					this.pollTimer = setTimeout(() => this.poll(scanId, requestId), 1500);
					return;
				}

				this.scanning = false;

				if (data.status === "error") {
					this.scanError = data.error || this.$t("yealink-net-scan-error");
				} else {
					this.$emit("scan-update", data);
					// A scan may have written an initial/drift entry
					if (data.historyEntryType) this.fetchHistory();
				}
			} catch (err) {
				if (requestId !== this.scanRequestId) return;
				console.error("Failed to poll network scan:", err);
				this.scanError = err.response?.data?.message || this.$t("yealink-net-scan-error");
				this.scanning = false;
			}
		},

		async fetchHistory() {
			this.history.loading = true;
			try {
				const { data } = await this.axios.get(`/devops/yealink/network/history${this.siteQuery()}`);

				this.history.entries = data.entries || [];
				this.history.entryCount = data.entryCount || this.history.entries.length;
			} catch (err) {
				console.error("Failed to fetch network history:", err);
			} finally {
				this.history.loading = false;
			}
		},

		defaultStunFor(transport) {
			return transport !== 0;
		},

		selectTransport(value) {
			if (this.selectedTransport === value) {
				this.selectedTransport = null;
				return;
			}
			this.selectedTransport = value;
			this.stunEnabled = this.defaultStunFor(value);
		},

		async confirmApply() {
			if (this.selectedTransport === null) return;
			this.applying = true;
			try {
				const { data } = await this.axios.post(`/devops/yealink/network/apply${this.siteQuery()}`, {
					transport: this.selectedTransport,
					stun: this.stunEnabled
				});

				this.applyResult = data;
				this.applyDialog = false;
				this.selectedTransport = null;
				this.$success(this.$t("yealink-net-apply-success", { pushed: data.pushed, total: data.total }));
				this.$emit("applied", data);
				this.fetchHistory();
				this.startRescanCountdown();
			} catch (err) {
				console.error("Failed to apply network transport:", err);
				this.$error(err.response?.data?.message || this.$t("yealink-net-apply-error"));
			} finally {
				this.applying = false;
			}
		},

		async confirmReboot() {
			this.rebooting = true;
			try {
				const { data } = await this.axios.post(`/devops/yealink/devices/reboot-all${this.siteQuery()}`);

				this.rebootResult = data;
				this.rebootDialog = false;
				this.$success(this.$t("yealink-net-reboot-all-success", { rebooted: data.rebooted, skipped: data.skipped, failed: data.failed }));
				this.$emit("rebooted", data);
			} catch (err) {
				console.error("Failed to reboot all devices:", err);
				this.$error(err.response?.data?.message || this.$t("yealink-net-reboot-all-error"));
			} finally {
				this.rebooting = false;
			}
		},

		toggleEntry(id) {
			this.$set(this.expandedEntries, id, !this.expandedEntries[id]);
		},

		// What a single drifted phone should be re-pushed to: the site-wide value when
		// uniform, otherwise what the phone's own accounts already ask for.
		repairTarget(d) {
			if (d.state !== "ok") return null;
			let transport = null;

			if (typeof this.summary.transport === "number") transport = this.summary.transport;
			else if (typeof d.transport === "number") transport = d.transport;
			if (transport === null) return null;

			let stun;

			if (typeof this.summary.stun === "boolean") stun = this.summary.stun;
			else stun = d.accounts.length > 0 && d.accounts.every(a => a.natTraversal === 1);

			return { transport, stun };
		},

		async repairDevice(d) {
			const target = this.repairTarget(d);

			if (!target) return;
			this.repairingIds = [...this.repairingIds, d.deviceId];
			try {
				const { data } = await this.axios.post(`/devops/yealink/network/apply${this.siteQuery()}`, {
					transport: target.transport,
					stun: target.stun,
					deviceIds: [d.deviceId]
				});

				this.applyResult = data;
				this.$success(this.$t("yealink-net-apply-success", { pushed: data.pushed, total: data.total }));
				this.$emit("applied", data);
				this.fetchHistory();
				this.startRescanCountdown();
			} catch (err) {
				console.error("Failed to re-push network config:", err);
				this.$error(err.response?.data?.message || this.$t("yealink-net-apply-error"));
			} finally {
				this.repairingIds = this.repairingIds.filter(id => id !== d.deviceId);
			}
		},

		// --- labels & colors -------------------------------------------------

		transportLabel(value) {
			if (value === "mixed") return this.$t("yealink-net-mixed");
			if (value === null || value === undefined) return "—";
			return { 0: "UDP", 1: "TCP", 2: "TLS", 3: "DNS-NAPTR" }[value] || `#${value}`;
		},

		transportColor(value) {
			if (value === "mixed") return MIXED_COLOR;
			if (value === null || value === undefined) return UNKNOWN_COLOR;
			return TRANSPORT_COLORS[value] || UNKNOWN_COLOR;
		},

		stunLabel(value) {
			if (value === "mixed") return this.$t("yealink-net-mixed");
			if (value === null || value === undefined) return "—";
			return value ? this.$t("yealink-net-stun-on") : this.$t("yealink-net-stun-off");
		},

		stunShort(value) {
			if (value === null || value === undefined) return "—";
			return value ? this.$t("yealink-net-stun") : this.$t("yealink-net-no-stun");
		},

		stunColor(value) {
			if (value === "mixed") return MIXED_COLOR;
			if (value === null || value === undefined) return UNKNOWN_COLOR;
			return value ? "#16a34a" : "#64748b";
		},

		stateColor(state) {
			return { ok: "#16a34a", offline: "#9ca3af", error: "#dc2626", timeout: "#dc2626" }[state] || "#9ca3af";
		},

		stateIcon(state) {
			return { ok: "mdi-check", offline: "mdi-power-plug-off", error: "mdi-alert", timeout: "mdi-timer-alert-outline" }[state] || "mdi-help";
		},

		entryIcon(type) {
			return { initial: "mdi-flag-checkered", change: "mdi-account-edit", detected: "mdi-source-branch" }[type] || "mdi-circle-small";
		},

		entryTitle(entry) {
			switch (entry.type) {
				case "initial": return this.$t("yealink-net-history-initial");
				case "change": return this.$t("yealink-net-history-change", { name: entry.by?.name || "?" });
				case "detected": return this.$t("yealink-net-history-detected");
				default: return entry.type;
			}
		},

		formatDateTime(ms) {
			if (!ms) return "—";
			return moment(ms).format("YYYY-MM-DD HH:mm:ss");
		},

		formatTime(ms) {
			if (!ms) return "—";
			return moment(ms).format("HH:mm:ss");
		},

		fromNow(ms) {
			const locale = (this.$i18n?.locale || "fr-FR").split("-")[0];

			return moment(ms).locale(locale).fromNow();
		}
	}
};
</script>

<style scoped>
.net-toolbox {
	background: linear-gradient(180deg, #f6fbfc 0%, #ffffff 100%);
	border-bottom: 1px solid #e0eef1;
	flex-shrink: 0;
	max-height: 50vh;
	overflow-y: auto;
}

.net-collapse-btn {
	color: #6b7f88 !important;
}

.net-collapsed-strip {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px;
	padding: 0 12px 8px;
	cursor: pointer;
}

.net-collapsed-text {
	font-size: 11px;
	color: #5a6b73;
}

.net-collapsed-warn {
	display: inline-flex;
	align-items: center;
	font-size: 11px;
	font-weight: 700;
	color: #92400e;
}

/* Header */
.net-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 6px 12px 6px;
	flex-wrap: wrap;
}

.net-header-left {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.net-header-icon {
	width: 24px;
	height: 24px;
	border-radius: 6px;
	background: linear-gradient(135deg, #00acc1, #00838f);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 2px 6px rgba(0, 172, 193, 0.35);
	flex-shrink: 0;
}

.net-header-icon .v-icon {
	font-size: 15px !important;
}

.net-header-text {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.net-title {
	font-size: 12.5px;
	font-weight: 700;
	color: #16323a;
	letter-spacing: 0.2px;
}

.net-header-right {
	display: flex;
	align-items: center;
	gap: 4px;
	flex-wrap: wrap;
}

.net-fresh {
	display: inline-flex;
	align-items: center;
	font-size: 10.5px;
	font-weight: 600;
	color: #16a34a;
	background: rgba(22, 163, 74, 0.08);
	border-radius: 12px;
	padding: 2px 8px;
	margin-right: 4px;
}

.net-header-btn {
	text-transform: none !important;
	letter-spacing: 0 !important;
	font-size: 11.5px !important;
	color: #3c5560 !important;
	height: 26px !important;
	padding: 0 8px !important;
}

.net-header-btn.is-active {
	background: rgba(0, 172, 193, 0.1);
	color: #00838f !important;
}

.net-count-pill {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 18px;
	height: 18px;
	padding: 0 5px;
	margin-left: 6px;
	border-radius: 9px;
	background: rgba(0, 172, 193, 0.15);
	color: #00838f;
	font-size: 10px;
	font-weight: 700;
}

.net-count-pill.warn {
	background: rgba(217, 119, 6, 0.15);
	color: #b45309;
}

/* Scanning */
.net-scanning {
	padding: 0 12px 10px;
}

.net-scanning-text {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 6px;
	font-size: 11.5px;
	color: #2c3e50;
	font-weight: 500;
}

.net-scanning-hint {
	font-size: 10.5px;
	color: #8a94a6;
	font-weight: 400;
	margin-left: 4px;
}

.net-scan-pills {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 8px;
	max-height: 64px;
	overflow-y: auto;
}

.net-scan-pill {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 2px 7px;
	border-radius: 10px;
	background: #fff;
	border: 1px solid #e3eaee;
	font-size: 10.5px;
	color: #3c5560;
	max-width: 160px;
}

.net-scan-pill.state-pending {
	border-style: dashed;
}

.net-scan-pill.state-ok {
	border-color: rgba(22, 163, 74, 0.35);
}

.net-scan-pill.state-error,
.net-scan-pill.state-timeout {
	border-color: rgba(220, 38, 38, 0.35);
}

.net-scan-pill-name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.net-scan-pill-value {
	font-weight: 700;
	font-size: 10px;
}

/* Body */
.net-body {
	padding: 0 12px 10px;
}

.net-card {
	display: flex;
	gap: 12px;
	background: #fff;
	border: 1px solid #e3eaee;
	border-radius: 10px;
	padding: 10px 12px;
	box-shadow: 0 1px 3px rgba(16, 42, 51, 0.06);
}

.net-state {
	flex: 0 0 42%;
	min-width: 0;
	display: flex;
	flex-direction: column;
}

.net-divider {
	width: 1px;
	background: #e3eaee;
	flex-shrink: 0;
}

.net-switch {
	flex: 1 1 auto;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

@media (max-width: 760px) {
	.net-card {
		flex-direction: column;
	}

	.net-state {
		flex-basis: auto;
	}

	.net-divider {
		width: auto;
		height: 1px;
	}
}

/* Tiles */
.net-tiles {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
}

.net-tile {
	--tile-color: #9ca3af;
	position: relative;
	border-radius: 8px;
	padding: 6px 10px 6px 12px;
	background: color-mix(in srgb, var(--tile-color) 10%, #fff);
	border: 1px solid color-mix(in srgb, var(--tile-color) 35%, #fff);
	display: flex;
	flex-direction: column;
	min-width: 0;
	overflow: hidden;
}

.net-tile::before {
	content: "";
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: 3px;
	background: var(--tile-color);
}

.net-tile-label {
	font-size: 9px;
	text-transform: uppercase;
	letter-spacing: 0.8px;
	font-weight: 700;
	color: #5a6b73;
}

.net-tile-value {
	font-size: 20px;
	font-weight: 800;
	line-height: 1.1;
	color: var(--tile-color);
	letter-spacing: -0.4px;
}

.net-tile-sub {
	font-size: 10px;
	color: #5a6b73;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

/* Distribution */
.net-dist {
	margin-top: 8px;
}

.net-dist-bar {
	display: flex;
	height: 6px;
	border-radius: 4px;
	overflow: hidden;
	background: #eef2f5;
	gap: 2px;
}

.net-dist-seg {
	display: block;
	height: 100%;
	transition: width 0.3s ease;
	min-width: 3px;
}

.net-dist-legend {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 2px 10px;
	margin-top: 5px;
	font-size: 10.5px;
	color: #3c5560;
}

.net-legend-item {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.net-legend-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	display: inline-block;
}

/* Attention */
.net-attention-toggle {
	display: inline-flex;
	align-items: center;
	margin-left: auto;
	padding: 1px 6px;
	border-radius: 8px;
	border: none;
	background: rgba(245, 158, 11, 0.12);
	color: #92400e;
	font-size: 10.5px;
	font-weight: 700;
	cursor: pointer;
	outline: none;
}

.net-attention {
	margin-top: 6px;
	padding: 6px 8px;
	border-radius: 6px;
	background: rgba(245, 158, 11, 0.08);
	border: 1px dashed rgba(245, 158, 11, 0.4);
	max-height: 96px;
	overflow-y: auto;
}

.net-attention-row {
	display: flex;
	justify-content: space-between;
	gap: 8px;
	font-size: 10.5px;
	color: #3c5560;
	padding: 1px 0;
}

.net-attention-name {
	font-weight: 600;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.net-attention-reason {
	color: #92400e;
	text-align: right;
	margin-left: auto;
}

.net-attention-actions {
	display: inline-flex;
	align-items: center;
	gap: 2px;
	flex-shrink: 0;
}

/* Switch */
.net-switch-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 6px 8px;
}

.net-switch-label {
	font-size: 11px;
	font-weight: 700;
	color: #16323a;
	white-space: nowrap;
}

.net-transport-options {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	flex-wrap: wrap;
}

.net-transport-btn {
	--opt-color: #9ca3af;
	position: relative;
	display: inline-flex;
	align-items: center;
	gap: 4px;
	height: 26px;
	padding: 0 10px;
	border-radius: 13px;
	border: 1.5px solid color-mix(in srgb, var(--opt-color) 45%, #fff);
	background: color-mix(in srgb, var(--opt-color) 8%, #fff);
	color: var(--opt-color);
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 0.3px;
	cursor: pointer;
	transition: all 0.15s ease;
	outline: none;
}

.net-transport-btn:hover:not(:disabled) {
	border-color: var(--opt-color);
}

.net-transport-btn.is-selected {
	background: var(--opt-color);
	border-color: var(--opt-color);
	color: #fff;
	box-shadow: 0 2px 8px color-mix(in srgb, var(--opt-color) 40%, transparent);
}

.net-transport-btn.is-advanced {
	border-style: dashed;
}

.net-transport-btn:disabled {
	opacity: 0.6;
	cursor: default;
}

.net-transport-current {
	position: absolute;
	top: -3px;
	right: -3px;
	width: 9px;
	height: 9px;
	border-radius: 50%;
	background: #16323a;
	border: 2px solid #fff;
}

.net-advanced-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	border: 1px solid #e3eaee;
	background: #fff;
	color: #8a94a6;
	cursor: pointer;
	outline: none;
	transition: all 0.15s ease;
}

.net-advanced-btn:hover,
.net-advanced-btn.is-open {
	border-color: #00acc1;
	background: rgba(0, 172, 193, 0.08);
}

.net-advanced-btn .v-icon {
	color: inherit !important;
}

.net-advanced-btn.is-open .v-icon {
	color: #00838f !important;
}

.net-stun-row {
	padding: 2px 8px;
	border-radius: 8px;
	background: #f6f9fa;
	border: 1px solid #e3eaee;
}

.net-stun-row.is-disabled {
	opacity: 0.6;
}

.net-stun-switch {
	margin: 0 !important;
	padding: 0 !important;
}

.net-stun-label {
	display: inline-flex;
	align-items: baseline;
	gap: 6px;
	line-height: 1.2;
}

.net-stun-label strong {
	font-size: 11.5px;
	color: #16323a;
}

.net-stun-server {
	font-size: 10px;
	color: #6b7f88;
	font-family: 'SF Mono', 'Fira Code', monospace;
}

.net-stun-default {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	border-radius: 50%;
	color: #00838f;
	background: rgba(0, 172, 193, 0.1);
}

.net-stun-default .v-icon {
	color: inherit !important;
}

.net-stun-default.is-override {
	color: #92400e;
	background: rgba(245, 158, 11, 0.12);
}

.net-tls-warn {
	display: inline-flex;
	align-items: center;
	margin-left: auto;
	font-size: 10.5px;
	font-weight: 700;
	color: #b91c1c;
}

.net-impact {
	display: flex;
	flex-wrap: wrap;
	gap: 4px 10px;
	font-size: 10.5px;
}

.net-impact-item {
	display: inline-flex;
	align-items: center;
	font-weight: 600;
}

.net-impact-item.change {
	color: #b45309;
}

.net-impact-item.same {
	color: #15803d;
}

.net-impact-item.offline {
	color: #6b7280;
}

.net-impact-dialog {
	margin-top: 4px;
}

.net-apply-btn,
.net-reboot-btn {
	text-transform: none !important;
	letter-spacing: 0 !important;
	font-weight: 600;
}

.net-apply-btn {
	flex: 1 1 auto;
}

.net-banner {
	margin: 8px 0 0 !important;
}

.net-banner-row {
	gap: 8px 16px;
}

.net-countdown {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
}

.net-countdown-num {
	font-size: 11px;
	font-weight: 800;
	color: #00838f;
}

.net-countdown-text {
	font-size: 11px;
	font-weight: 600;
	color: #00838f;
	white-space: nowrap;
}

/* Confirm dialog */
.net-confirm-target {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 10px;
	margin-bottom: 12px;
	border-radius: 10px;
	background: #f6f9fa;
}

.net-confirm-big {
	font-size: 22px;
	font-weight: 800;
	letter-spacing: -0.3px;
}

.net-confirm-plus {
	font-size: 18px;
	color: #9ca3af;
}

/* History */
.net-history {
	margin-top: 12px;
	padding-top: 10px;
	border-top: 1px dashed #dbe5e9;
}

.net-history-header {
	display: flex;
	align-items: center;
	margin-bottom: 8px;
}

.net-history-title {
	font-size: 12px;
	font-weight: 700;
	color: #16323a;
}

.net-history-count {
	margin-left: 6px;
	font-size: 10px;
	font-weight: 700;
	color: #5a6b73;
	background: #eef2f5;
	border-radius: 8px;
	padding: 1px 6px;
}

.net-history-empty {
	font-size: 11.5px;
	color: #8a94a6;
	padding: 6px 0;
}

.net-timeline {
	position: relative;
	padding-left: 6px;
}

.net-timeline::before {
	content: "";
	position: absolute;
	left: 15px;
	top: 6px;
	bottom: 6px;
	width: 2px;
	background: #e3eaee;
}

.net-entry {
	display: flex;
	gap: 10px;
	margin-bottom: 8px;
	position: relative;
}

.net-entry-dot {
	width: 20px;
	height: 20px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	z-index: 1;
	margin-top: 4px;
	box-shadow: 0 0 0 3px #fff;
}

.type-initial .net-entry-dot { background: #00acc1; }
.type-change .net-entry-dot { background: #2563eb; }
.type-detected .net-entry-dot { background: #f59e0b; }

.net-entry-body {
	flex: 1;
	min-width: 0;
	background: #fff;
	border: 1px solid #e3eaee;
	border-radius: 8px;
	padding: 6px 10px;
	cursor: pointer;
	transition: border-color 0.15s ease;
}

.net-entry-body:hover {
	border-color: #b8d5dc;
}

.type-detected .net-entry-body {
	border-left: 3px solid #f59e0b;
}

.type-change .net-entry-body {
	border-left: 3px solid #2563eb;
}

.type-initial .net-entry-body {
	border-left: 3px solid #00acc1;
}

.net-entry-top {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	flex-wrap: wrap;
}

.net-entry-title {
	font-size: 12px;
	font-weight: 700;
	color: #16323a;
}

.net-entry-badges {
	display: inline-flex;
	gap: 4px;
}

.net-mini-badge {
	--badge-color: #9ca3af;
	font-size: 10px;
	font-weight: 700;
	padding: 1px 7px;
	border-radius: 8px;
	color: var(--badge-color);
	background: color-mix(in srgb, var(--badge-color) 12%, #fff);
	border: 1px solid color-mix(in srgb, var(--badge-color) 35%, #fff);
}

.net-entry-meta {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 2px 12px;
	margin-top: 3px;
	font-size: 10.5px;
	color: #6b7f88;
}

.net-entry-meta > span {
	display: inline-flex;
	align-items: center;
}

.net-entry-date {
	font-family: 'SF Mono', 'Fira Code', monospace;
	color: #3c5560;
}

.net-entry-chevron {
	margin-left: auto;
}

.net-entry-details {
	margin-top: 6px;
	padding-top: 6px;
	border-top: 1px dashed #e3eaee;
	cursor: default;
	max-height: 220px;
	overflow-y: auto;
}

.net-change-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 2px 4px;
	font-size: 11px;
	padding: 2px 0;
	color: #3c5560;
}

.net-change-name {
	font-weight: 600;
	min-width: 120px;
	max-width: 200px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.net-change-from {
	color: #9ca3af;
	text-decoration: line-through;
}

.net-change-to {
	font-weight: 700;
}

.net-change-result {
	margin-left: auto;
	font-size: 10px;
	font-weight: 700;
	text-transform: uppercase;
	padding: 0 6px;
	border-radius: 6px;
}

.net-change-result.pushed {
	color: #15803d;
	background: rgba(22, 163, 74, 0.1);
}

.net-change-result.failed {
	color: #b91c1c;
	background: rgba(220, 38, 38, 0.1);
}

@media (max-width: 600px) {
	.net-header {
		padding: 8px 12px 6px;
	}

	.net-body,
	.net-scanning {
		padding-left: 12px;
		padding-right: 12px;
	}

	.net-tile-value {
		font-size: 22px;
	}

	.net-actions .v-btn {
		width: 100%;
	}
}
</style>
